import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { KHITAN_CHILD_STATUSES, today } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Download, Upload } from "lucide-react";

const dayFromDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("id-ID", { weekday: "long" });
};

const DOC_FIELDS = [
  { key: "akta", label: "Unggah Akta Kelahiran / KIA", testid: "khitan-file-akta" },
  { key: "ktp", label: "Unggah KTP Orangtua / Wali", testid: "khitan-file-ktp" },
  { key: "kk", label: "Unggah KK Orangtua / Wali", testid: "khitan-file-kk" },
  { key: "rekomendasi", label: "Unggah Rekomendasi (LAZISNU Ranting / Desa / Kepala Madrasah)", testid: "khitan-file-rekomendasi" },
  { key: "persetujuan", label: "Unggah Surat Persetujuan (sudah ditandatangani)", testid: "khitan-file-persetujuan" },
];

export default function Khitan() {
  const [form, setForm] = useState({
    participant_name: "",
    nik: "",
    parent_name: "",
    child_status: "yatim",
    parent_phone: "",
    desa: "",
    dukuh: "",
    rt: "",
    rw: "",
    date: today(),
    time: "",
  });
  const [files, setFiles] = useState({ akta: null, ktp: null, kk: null, rekomendasi: null, persetujuan: null });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.participant_name || !form.parent_name || !form.parent_phone) {
      toast.error("Lengkapi nama peserta, nama orangtua/wali, dan No. WA");
      return;
    }
    const missing = DOC_FIELDS.filter((d) => !files[d.key]);
    if (missing.length) {
      toast.error(`Dokumen belum lengkap: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("participant_name", form.participant_name);
    fd.append("nik", form.nik);
    fd.append("parent_name", form.parent_name);
    fd.append("child_status", form.child_status);
    fd.append("parent_phone", form.parent_phone);
    fd.append("desa", form.desa);
    fd.append("dukuh", form.dukuh);
    fd.append("rt", form.rt);
    fd.append("rw", form.rw);
    fd.append("day", dayFromDate(form.date));
    fd.append("date", form.date);
    fd.append("time", form.time);
    DOC_FIELDS.forEach((d) => fd.append(d.key, files[d.key]));
    try {
      const { data } = await api.post("/public/khitan-requests", fd);
      toast.success("Pendaftaran khitan berhasil dikirim");
      const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
      if (sent > 0) toast.success(`Notifikasi WA terkirim ke ${sent} nomor`);
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="khitan-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Khitan Sehat Gratis</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Kecamatan Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="khitan-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {success ? (
          <Card>
            <CardContent className="p-10 text-center" data-testid="khitan-success">
              <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-emerald-700" />
              <h2 className="font-heading text-xl font-bold text-slate-900">Pendaftaran Berhasil Terkirim</h2>
              <p className="mt-2 text-sm text-slate-500">
                Data Ananda sudah masuk. Selanjutnya akan dilakukan asesmen oleh petugas kami. Konfirmasi dikirim ke No. WA Orangtua/Wali.
              </p>
              <Link to="/">
                <Button className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="khitan-success-back">Kembali ke Beranda</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Form Permohonan Khitan Gratis</CardTitle>
              <p className="text-xs text-slate-500">
                Unduh{" "}
                <a href="/dokumen/contoh-surat-persetujuan-khitan.docx" download className="font-semibold text-emerald-800 underline" data-testid="khitan-template-link">
                  <Download className="inline h-3.5 w-3.5 mr-0.5" />
                  Contoh Surat Persetujuan
                </a>{" "}
                untuk diisi dan ditandatangani, lalu unggah bersama dokumen lainnya.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="khitan-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nama Peserta *</Label>
                    <Input data-testid="khitan-name-input" value={form.participant_name} onChange={(e) => setForm({ ...form, participant_name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>NIK</Label>
                    <Input data-testid="khitan-nik-input" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Nama Orangtua / Wali *</Label>
                    <Input data-testid="khitan-parent-input" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Status Anak</Label>
                    <Select value={form.child_status} onValueChange={(v) => setForm({ ...form, child_status: v })}>
                      <SelectTrigger data-testid="khitan-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {KHITAN_CHILD_STATUSES.map((s) => (
                          <SelectItem key={s.key} value={s.key} data-testid={`khitan-status-${s.key}`}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>No. WA Orangtua / Wali *</Label>
                    <Input data-testid="khitan-phone-input" placeholder="08xxxxxxxxxx" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Desa</Label>
                    <Input data-testid="khitan-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Dukuh</Label>
                      <Input data-testid="khitan-dukuh-input" value={form.dukuh} onChange={(e) => setForm({ ...form, dukuh: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>RT</Label>
                      <Input data-testid="khitan-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>RW</Label>
                      <Input data-testid="khitan-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Tanggal</Label>
                      <Input type="date" data-testid="khitan-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Hari</Label>
                      <Input data-testid="khitan-day-input" value={dayFromDate(form.date)} readOnly className="bg-slate-50" />
                    </div>
                    <div className="space-y-2">
                      <Label>Jam</Label>
                      <Input type="time" data-testid="khitan-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {DOC_FIELDS.map((d) => (
                    <div key={d.key} className="space-y-2">
                      <Label className="text-xs">{d.label} *</Label>
                      <label
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm transition-colors duration-200 ${
                          files[d.key] ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-slate-300 text-slate-500 hover:border-emerald-400"
                        }`}
                      >
                        <Upload className="h-4 w-4 shrink-0" />
                        <span className="truncate">{files[d.key] ? files[d.key].name : "Pilih file (gambar/PDF)"}</span>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          data-testid={d.testid}
                          onChange={(e) => setFiles({ ...files, [d.key]: e.target.files?.[0] || null })}
                        />
                      </label>
                    </div>
                  ))}
                </div>

                <Button type="submit" data-testid="khitan-submit-button" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Pendaftaran"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
